import { z } from "zod";

/** Every record type whose key grants the app stores. */
export const KEY_GRANT_RESOURCE_TYPES = [
  "overallVision",
  "categoryOfLife",
  "bucketListItem",
  "goal",
  "obstacle",
  "sessionNote",
  "engagementNote",
  "sessionCheckIn",
  "homeworkReflection",
  "engagement",
] as const;

export type KeyGrantResourceType = (typeof KEY_GRANT_RESOURCE_TYPES)[number];

/** The record types the Life Design "share with coaches" switches cover. */
export const LIFE_DESIGN_SHARED_RESOURCE_TYPES = [
  "overallVision",
  "categoryOfLife",
  "bucketListItem",
  "goal",
] as const satisfies readonly KeyGrantResourceType[];

export type LifeDesignSharedResourceType =
  (typeof LIFE_DESIGN_SHARED_RESOURCE_TYPES)[number];

/** A client shares Life Design with each active coach, so a few is normal. */
const MAX_SHARED_GRANTS = 50;

const base64Url = z.string().regex(/^[A-Za-z0-9_-]+$/);

const WrappedDekShape = z.object({
  ciphertext: base64Url,
  iv: base64Url,
  tag: base64Url,
  ephemeralPublicKey: base64Url,
});

function isWrappedDekJson(value: string): boolean {
  try {
    return WrappedDekShape.safeParse(JSON.parse(value)).success;
  } catch {
    return false;
  }
}

/** A record's key wrapped for one person: the JSON that `wrapDek` returns. */
export const wrappedDekJson = z
  .string()
  .max(1024, "Wrapped key is too long")
  .refine(isWrappedDekJson, "Value is not a wrapped key");

/**
 * The keys a save writes together with its record. Every save that sends new
 * ciphertext sends its new key, so the record and its keys change together
 * or not at all.
 */
export const KeyGrantsSchema = z.object({
  /** The key wrapped for the person saving. */
  own: wrappedDekJson,
  /** The key wrapped for each person the record is shared with. */
  shared: z
    .array(
      z.object({ userId: z.string().uuid(), encryptedDek: wrappedDekJson }),
    )
    .max(MAX_SHARED_GRANTS),
});

export type KeyGrants = z.infer<typeof KeyGrantsSchema>;
